import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "listbox", "clearBtn"]
  static values = {
    series: Array,
    createUrl: String,
    placeholder: { type: String, default: "No series" },
    createText: { type: String, default: 'Create "%{name}"' }
  }

  connect() {
    this.selectedId = this.hiddenTarget.value || ""
    this.selectedTitle = this.inputTarget.value || ""
    this.isOpen = false
    this.highlightedIndex = -1
    this.creating = false
    this.filteredItems = []

    this._handleClickOutside = (event) => {
      if (!this.element.contains(event.target)) {
        this.close()
      }
    }
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
  }

  // --- Filtering & Rendering ---

  filter() {
    const raw = this.inputTarget.value.trim()
    const query = raw.toLowerCase()
    this.updateClearBtn()

    if (!query) {
      this.filteredItems = this.seriesValue
    } else {
      this.filteredItems = this.seriesValue.filter(s =>
        s.title.toLowerCase().includes(query)
      )
    }

    this.renderList(raw)
    if (!this.isOpen) this.open()
  }

  renderList(query = "") {
    const listbox = this.listboxTarget
    listbox.innerHTML = ""
    this.highlightedIndex = -1

    // "No series" option
    const noneItem = document.createElement("li")
    noneItem.className = "combobox-option combobox-option-empty"
    noneItem.setAttribute("role", "option")
    noneItem.textContent = this.placeholderValue
    noneItem.dataset.id = ""
    noneItem.addEventListener("click", () => this.selectItem("", ""))
    listbox.appendChild(noneItem)

    // Matched series
    for (const s of this.filteredItems) {
      const li = document.createElement("li")
      li.className = "combobox-option"
      if (String(s.id) === String(this.selectedId)) {
        li.classList.add("is-selected")
      }
      li.setAttribute("role", "option")
      li.textContent = s.title
      li.dataset.id = s.id
      li.addEventListener("click", () => this.selectItem(s.id, s.title))
      listbox.appendChild(li)
    }

    // "Create new" option
    const trimmedQuery = query.trim()
    if (trimmedQuery) {
      const exactMatch = this.seriesValue.some(s =>
        s.title.toLowerCase() === trimmedQuery.toLowerCase()
      )
      if (!exactMatch) {
        const createLi = document.createElement("li")
        createLi.className = "combobox-create-option"
        createLi.setAttribute("role", "option")
        createLi.textContent = this.createTextValue.replace("%{name}", trimmedQuery)
        createLi.dataset.createName = trimmedQuery
        createLi.addEventListener("click", () => this.createSeries(trimmedQuery))
        listbox.appendChild(createLi)
      }
    }
  }

  // --- Open / Close ---

  open() {
    if (this.isOpen) return
    this.isOpen = true

    if (this.selectedId) {
      this.inputTarget.select()
      this.filteredItems = this.seriesValue
      this.renderList("")
    } else {
      const raw = this.inputTarget.value.trim()
      const query = raw.toLowerCase()
      if (!query) {
        this.filteredItems = this.seriesValue
      } else {
        this.filteredItems = this.seriesValue.filter(s =>
          s.title.toLowerCase().includes(query)
        )
      }
      this.renderList(raw)
    }

    this.positionListbox()
    this.listboxTarget.classList.add("is-open")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  positionListbox() {
    const rect = this.inputTarget.getBoundingClientRect()
    const footer = document.querySelector(".editor-footer")
    const footerHeight = footer ? footer.offsetHeight : 0
    const spaceBelow = window.innerHeight - rect.bottom - footerHeight
    const listboxHeight = 208
    this.listboxTarget.classList.toggle("is-above", spaceBelow < listboxHeight)
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.listboxTarget.classList.remove("is-open")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.highlightedIndex = -1

    const inputVal = this.inputTarget.value.trim()
    if (!inputVal) {
      this.selectItem("", "")
    } else {
      const match = this.seriesValue.find(s =>
        s.title.toLowerCase() === inputVal.toLowerCase()
      )
      if (match) {
        this.selectItem(match.id, match.title)
      } else {
        this.inputTarget.value = this.selectedTitle
        this.updateClearBtn()
      }
    }
  }

  // --- Selection ---

  selectItem(id, title) {
    this.selectedId = String(id)
    this.selectedTitle = title
    this.hiddenTarget.value = id
    this.inputTarget.value = title
    this.updateClearBtn()
    this.close()
    this.hiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clear() {
    this.selectItem("", "")
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  // --- Create ---

  async createSeries(title) {
    if (this.creating) return false
    this.creating = true

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ title })
      })

      if (!response.ok) {
        this.creating = false
        return false
      }

      const data = await response.json()
      const updated = [...this.seriesValue, { id: data.id, title: data.title }]
      this.seriesValue = updated
      this.selectItem(data.id, data.title)
      return true
    } catch {
      return false
    } finally {
      this.creating = false
    }
  }

  // --- Keyboard Navigation ---

  handleKeydown(event) {
    if (!this.isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault()
      this.open()
      return
    }

    if (!this.isOpen) {
      if (event.key === "Enter") event.preventDefault()
      return
    }

    const options = this.listboxTarget.querySelectorAll("[role='option']")
    const count = options.length

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, count - 1)
        this.updateHighlight(options)
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
        this.updateHighlight(options)
        break
      case "Enter":
        event.preventDefault()
        if (this.highlightedIndex >= 0 && this.highlightedIndex < count) {
          options[this.highlightedIndex].click()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  updateHighlight(options) {
    options.forEach((opt, i) => {
      opt.classList.toggle("is-highlighted", i === this.highlightedIndex)
      if (i === this.highlightedIndex) {
        opt.scrollIntoView({ block: "nearest" })
      }
    })
  }

  // --- Clear Button ---

  updateClearBtn() {
    if (!this.hasClearBtnTarget) return
    const hasValue = this.inputTarget.value.trim() || this.selectedId
    this.clearBtnTarget.hidden = !hasValue
  }

  // --- Public API (for AI assistant) ---

  selectById(id) {
    const s = this.seriesValue.find(s => String(s.id) === String(id))
    if (s) {
      this.selectItem(s.id, s.title)
    }
  }

  async selectByTitleOrCreate(title) {
    const match = this.seriesValue.find(s =>
      s.title.toLowerCase().trim() === title.toLowerCase().trim()
    )
    if (match) {
      this.selectItem(match.id, match.title)
      return true
    } else {
      return await this.createSeries(title)
    }
  }

  addAndSelect(id, title) {
    if (!this.seriesValue.some(s => String(s.id) === String(id))) {
      this.seriesValue = [...this.seriesValue, { id, title }]
    }
    this.selectItem(id, title)
  }
}
